import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useReveal } from '@/lib/motion';

// Fallback used only when neither the prop nor the DB-fetched value provides
// a background_image. Matches the seed default so behavior is consistent
// before the seed completes / on broken DB reads.
const FALLBACK_BG = 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=2400&q=80';

export default function FastTrackSection({ fastTrack: fastTrackProp = null }) {
  const [data, setData] = useState(fastTrackProp);
  const reveal = useReveal();

  useEffect(() => {
    if (fastTrackProp) {
      setData(fastTrackProp);
      return;
    }
    let alive = true;
    api.get('/public/fast-track').then((r) => {
      if (alive) setData(r.data || null);
    }).catch(() => { /* render null on error — keeps page clean */ });
    return () => { alive = false; };
  }, [fastTrackProp]);

  if (!data) return null;
  if (data.visible === false) return null;

  const pillars = Array.isArray(data.pillars) ? data.pillars.slice(0, 3) : [];
  const primaryUrl = data.primary_cta_url || '/fast-track';
  const isAbsoluteCta = /^https?:\/\//.test(primaryUrl);
  // v3.20 — Legacy /fast-track placeholder route leads to a near-empty
  // ComingSoon page that confused real users tapping "EXPLORE FAST
  // TRACK" on mobile. Intercept that exact URL (and the empty/blank
  // fallback) so the button instead smooth-scrolls to the Investor
  // Fast Track card in the Experiences grid — whose flip-back now
  // contains the full verbatim Fast Track description.
  //
  // This intercept disengages automatically once admin sets
  // primary_cta_url to a real destination (any http(s):// URL or any
  // internal path other than /fast-track).
  const isLegacyFastTrackPath = (u) => {
    const v = (u || '').trim();
    return v === '' || v === '/fast-track';
  };
  const useScrollIntercept = isLegacyFastTrackPath(primaryUrl);
  const handleExploreScroll = (e) => {
    e.preventDefault();
    // Find the INVESTOR FAST TRACK card in the Experiences grid by
    // walking the experience-card list and matching its title. Title-
    // based lookup (rather than DOM index) is resilient to admin
    // reordering of the cards.
    let target = null;
    try {
      const cards = document.querySelectorAll('[data-testid="experience-card"]');
      for (const c of cards) {
        const h3 = c.querySelector('h3');
        if (h3 && h3.innerText.trim().toUpperCase() === 'INVESTOR FAST TRACK') {
          target = c;
          break;
        }
      }
    } catch (_) { /* fall through to section-header fallback */ }
    if (!target) target = document.querySelector('#experiences');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  const mailto = `mailto:${data.secondary_cta_email || ''}`;
  const bgImage = data.background_image && data.background_image.trim() !== ''
    ? data.background_image
    : FALLBACK_BG;

  return (
    <section id="fast-track" className="section-pad bg-card-dark relative overflow-hidden" data-testid="section-fast-track">
      {/* Background image layer */}
      <img
        src={fileUrl(bgImage)}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        aria-hidden="true"
      />
      {/* Dark scrim layer */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.85) 100%)' }}
        aria-hidden="true"
      />
      {/* Content layer */}
      <div ref={reveal.ref} className={`container-x relative z-10 ${reveal.simple ? 'reveal-simple' : 'reveal-up'} ${reveal.revealed ? 'is-in' : ''}`}>
        {/* v3.15 — Eyebrow size from settings.fast_track.eyebrow_size (a
            strict, bounded integer on FastTrackSettings — see server.py).
            Defensive validation: integer in [16, 48]. */}
        {data.eyebrow && (() => {
          const _raw = data.eyebrow_size;
          const eyebrowSize = typeof _raw === 'number' && Number.isInteger(_raw) && _raw >= 16 && _raw <= 48 ? _raw : 26;
          return (
            <div className="eyebrow eyebrow-lg" data-testid="fast-track-eyebrow" style={{ '--eyebrow-size': `${eyebrowSize}px` }}>{data.eyebrow}</div>
          );
        })()}
        {data.headline && (
          <h2 className="mt-2 font-display font-bold uppercase text-white leading-[1.05] tracking-tightish text-[36px] md:text-[44px] lg:text-[56px]" data-testid="fast-track-headline">
            {data.headline}
          </h2>
        )}
        {data.dek && (
          <p className="mt-3 md:mt-4 font-body text-white/80 text-[16px] md:text-[18px] leading-relaxed max-w-prose" data-testid="fast-track-dek">
            {data.dek}
          </p>
        )}
        {data.intro_paragraph && (
          <p className="mt-6 font-body text-white/90 text-[15px] md:text-[16px] leading-relaxed max-w-2xl" data-testid="fast-track-intro">
            {data.intro_paragraph}
          </p>
        )}

        {pillars.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="fast-track-pillars">
            {pillars.map((p, i) => (
              <div key={i} className="flex flex-col border-l-2 border-brand-orange/40 pl-4" data-testid={`fast-track-pillar-${i}`}>
                <div className="font-display font-bold uppercase text-white text-[20px] md:text-[24px] tracking-tightish leading-tight">
                  {p.label}
                </div>
                <div className="mt-2 font-body text-white/70 text-[15px] leading-relaxed">
                  {p.body}
                </div>
              </div>
            ))}
          </div>
        )}

        {data.global_reach_line && (
          <div className="mt-12 font-body font-semibold uppercase text-brand-orange text-[12px] md:text-[13px] tracking-wider2 max-w-2xl" data-testid="fast-track-global-reach">
            {data.global_reach_line}
          </div>
        )}

        <div className="mt-6" data-testid="fast-track-cta-row">
          {isAbsoluteCta ? (
            <a href={primaryUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="fast-track-primary-cta">
              {data.primary_cta_label || 'Explore'}
            </a>
          ) : useScrollIntercept ? (
            <button type="button" onClick={handleExploreScroll} className="btn-primary" data-testid="fast-track-primary-cta">
              {data.primary_cta_label || 'Explore'}
            </button>
          ) : (
            <Link to={primaryUrl} className="btn-primary" data-testid="fast-track-primary-cta">
              {data.primary_cta_label || 'Explore'}
            </Link>
          )}
        </div>

        {(data.secondary_prompt || data.secondary_cta_label) && (
          <div className="mt-6 font-body text-white/70 text-[14px] md:text-[15px]" data-testid="fast-track-secondary-row">
            {data.secondary_prompt && <span>{data.secondary_prompt} </span>}
            {data.secondary_cta_label && data.secondary_cta_email && (
              <a href={mailto} className="text-brand-orange font-semibold underline underline-offset-4 hover:opacity-80 transition" data-testid="fast-track-secondary-cta">
                {data.secondary_cta_label}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
